import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { usuarioProfileService, fichaCadastralService, ModuloResumo } from '@/services/api';
import { sections, genericColumns } from '@/pages/usuarioDetalhes/sections';
import { fichaCadastralFromResponse, emptyFichaCadastral } from '@/components/usuario/FichaCadastralForm';
import { Usuario } from '@/types';
import { CORES_SEMAFORO, estilizarCabecalho, autoWidth, zebra } from '@/lib/excelStyle';
import { colunasDoRelatorioUso, chavesCentralizadas } from '@/lib/relatorioUsoColunas';

const roleLabels: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Administrador',
  comum: 'Comum',
};

const formatDateTime = (iso: string | null) => {
  if (!iso) return 'Nunca acessou';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** Nomes de aba do Excel: máx. 31 caracteres, sem [ ] : * ? / \ */
export function sheetName(nome: string, usados: Set<string>): string {
  const base = nome.replace(/[[\]:*?/\\]/g, '').slice(0, 31) || 'Aba';
  let nomeFinal = base;
  let i = 2;
  while (usados.has(nomeFinal)) {
    nomeFinal = `${base.slice(0, 28)} (${i})`;
    i += 1;
  }
  usados.add(nomeFinal);
  return nomeFinal;
}

/**
 * Monta o workbook completo (dados da conta, empresas vinculadas, ficha
 * cadastral e uma aba por módulo com atividade detalhada) de um único
 * usuário. Reaproveitado tanto pelo "Exportar tudo" de UsuarioDetalhes.tsx
 * quanto pelo "Baixar tudo" (todos os usuários) de Usuarios.tsx.
 */
export async function buildUsuarioWorkbook(usuarioId: string, empresaId?: string): Promise<XLSX.WorkBook | null> {
  const [perfil, resumo, acessos, fichaResponse] = await Promise.all([
    usuarioProfileService.getPerfil(usuarioId),
    usuarioProfileService.getResumo(usuarioId, empresaId),
    usuarioProfileService.getAtividade(usuarioId, 'acessos', { page: 1, limit: 9999, empresaId }),
    fichaCadastralService.get(usuarioId),
  ]);

  if (!perfil) return null;

  const u = perfil.usuario;
  const ficha = fichaResponse.success ? fichaCadastralFromResponse(fichaResponse.data) : emptyFichaCadastral;
  const empresasVinculadas = (acessos.data ?? []).map((r) => ({
    empresa: (r.empresa as string | null) ?? 'Empresa removida',
    tipo: (r.tipo as string) ?? 'comum',
    departamento: (r.departamento as string | null) ?? null,
    ativo: Boolean(r.ativo),
  }));

  const usados = new Set<string>();
  const workbook = XLSX.utils.book_new();

  const dadosConta = [{
    Nome: u.nome,
    Email: u.email,
    Tipo: roleLabels[u.tipo] ?? u.tipo,
    Empresa: u.empresa?.nome ?? '',
    'Último acesso': formatDateTime(perfil.ultimo_acesso),
    'Total de acessos': perfil.total_acessos,
  }];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(dadosConta), sheetName('Dados da conta', usados));

  if (empresasVinculadas.length > 0) {
    const vinculos = empresasVinculadas.map((e) => ({
      Empresa: e.empresa,
      Tipo: roleLabels[e.tipo] ?? e.tipo,
      Departamento: e.departamento ?? '',
      Ativo: e.ativo ? 'Sim' : 'Não',
    }));
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(vinculos), sheetName('Empresas vinculadas', usados));
  }

  const fichaRow = [{
    CEP: ficha.cep ?? '', Logradouro: ficha.logradouro ?? '', Número: ficha.numero ?? '',
    Complemento: ficha.complemento ?? '', Bairro: ficha.bairro ?? '', Cidade: ficha.cidade ?? '',
    Estado: ficha.estado ?? '', 'Estado civil': ficha.estado_civil ?? '', Telefone: ficha.telefone ?? '',
    'Data de nascimento': ficha.data_nascimento ?? '', 'Nome do cônjuge': ficha.nome_conjuge ?? '',
    'Data admissão': ficha.data_admissao ?? '',
    'Contato de emergência': ficha.contato_emergencia_nome ?? '',
    'Telefone de emergência': ficha.contato_emergencia_telefone ?? '',
    'Parentesco (emergência)': ficha.contato_emergencia_parentesco ?? '',
    Filhos: ficha.filhos.map((f) => f.nome).join(', '),
    Habilidades: ficha.habilidades.map((h) => h.nome).join(', '),
    Cursos: ficha.cursos.map((c) => c.nome).join(', '),
  }];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(fichaRow), sheetName('Ficha cadastral', usados));

  for (const m of resumo.filter((mm) => mm.key !== 'acessos')) {
    const res = await usuarioProfileService.getAtividade(usuarioId, m.key, { page: 1, limit: 9999, empresaId });
    const config = sections[m.key];
    const cols = config?.columns ?? (res.data[0] ? genericColumns(res.data[0]) : []);
    const rows = res.data.map((row) => {
      const out: Record<string, unknown> = {};
      cols.forEach((c) => {
        const raw = row[c.key];
        out[c.label] = raw === null || raw === undefined ? '' : raw;
      });
      return out;
    });
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(rows.length > 0 ? rows : [{}]),
      sheetName(m.label, usados)
    );
  }

  return workbook;
}

// ---------------------------------------------------------------------------
// Relatório de Uso — "Base Detalhada" (aprovado com a usuária em 26/08/2026)
// ---------------------------------------------------------------------------

export type NivelSemaforo = keyof typeof CORES_SEMAFORO;

export const diasDesde = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
};

export const statusAcesso = (dias: number | null): NivelSemaforo | 'Nunca acessou' => {
  if (dias === null) return 'Nunca acessou';
  if (dias <= 7) return 'Alto' as NivelSemaforo; // reaproveita as mesmas 3 cores; rótulo real é mapeado abaixo
  if (dias <= 30) return 'Médio' as NivelSemaforo;
  return 'Baixo' as NivelSemaforo;
};

export const LABEL_STATUS_ACESSO: Record<NivelSemaforo, string> = {
  Alto: 'Ativo',
  Médio: 'Em acompanhamento',
  Baixo: 'Inativo',
};

/**
 * Nível de utilização e Produtividade (0-1) são placeholders — contam quantos dos 6 módulos
 * considerados (OKRs, Projetos, Calendário, Anexos em projetos — chave `cronogramas`, Fluxogramas, Organogramas) o usuário
 * tem atividade (`total > 0`). Ainda não validado com o Lucas o que "produtividade" deve
 * significar de verdade — ver docs/proposta-relatorio-uso-2026-08-26.md.
 */
export const MODULOS_PRODUTIVIDADE = ['okrs', 'projetos', 'calendario', 'cronogramas', 'fluxogramas', 'organogramas'];

export function recursosUtilizados(resumo: ModuloResumo[]): number {
  return MODULOS_PRODUTIVIDADE.filter((key) => (resumo.find((m) => m.key === key)?.total ?? 0) > 0).length;
}

export function nivelUtilizacao(recursos: number): NivelSemaforo {
  if (recursos >= 4) return 'Alto';
  if (recursos >= 2) return 'Médio';
  return 'Baixo';
}

export interface LinhaRelatorioUso {
  usuario: Usuario;
  resumo: ModuloResumo[];
  empresas: string;
  departamento: string;
}

export interface MetricasUsuario {
  id: string;
  nome: string;
  empresas: string;
  departamento: string;
  projetos: number;
  okrs: number;
  calendario: number;
  cronogramas: number;
  fluxogramas: number;
  organogramas: number;
  produtividade: number;
  dias: number | null;
  /** null = o resumo não trouxe notificações (só superadmin e a própria pessoa veem) — não é zero. */
  notificacoesNaoLidas: number | null;
  statusAcessoLabel: string;
  nivelUtilizacao: NivelSemaforo;
}

/** Calcula as mesmas métricas usadas no relatório de uso — reaproveitado pelo export e pelo dashboard. */
export function computeMetricasUsuario({ usuario, resumo, empresas, departamento }: LinhaRelatorioUso): MetricasUsuario {
  const totalDe = (key: string) => resumo.find((m) => m.key === key)?.total ?? 0;
  const notificacoes = resumo.find((m) => m.key === 'notificacoes');
  const ultimoAcesso = (usuario.ultimo_acesso as string | undefined) ?? null;
  const dias = diasDesde(ultimoAcesso);
  const recursos = recursosUtilizados(resumo);
  const nivel = nivelUtilizacao(recursos);
  const statusNivel = statusAcesso(dias);

  return {
    id: usuario.id,
    nome: usuario.nome,
    empresas,
    departamento,
    projetos: totalDe('projetos'),
    okrs: totalDe('okrs'),
    calendario: totalDe('calendario'),
    cronogramas: totalDe('cronogramas'),
    fluxogramas: totalDe('fluxogramas'),
    organogramas: totalDe('organogramas'),
    produtividade: Math.round((recursos / MODULOS_PRODUTIVIDADE.length) * 100) / 100,
    dias,
    notificacoesNaoLidas: notificacoes ? Number(notificacoes.extra?.nao_lidas ?? 0) : null,
    statusAcessoLabel: statusNivel === 'Nunca acessou' ? 'Nunca acessou' : LABEL_STATUS_ACESSO[statusNivel],
    nivelUtilizacao: nivel,
  };
}

/** Monta o workbook do relatório de uso (só a aba "Base Detalhada", formatada com semáforo). */
export async function buildRelatorioUsoWorkbook(
  linhas: LinhaRelatorioUso[],
  /** Obrigatório de propósito: quem chama decide (ver `veNotificacoesDeTodos`), sem critério automático. */
  { incluirNotificacoes }: { incluirNotificacoes: boolean },
): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet('Base Detalhada');

  const metricas = linhas.map((linha) => computeMetricasUsuario(linha));

  sheet.columns = colunasDoRelatorioUso(incluirNotificacoes);

  linhas.forEach((linha, i) => {
    const m = metricas[i];
    const ultimoAcesso = (linha.usuario.ultimo_acesso as string | undefined) ?? null;

    sheet.addRow({
      nome: m.nome,
      departamento: m.departamento,
      empresas: m.empresas,
      projetos: m.projetos,
      okrs: m.okrs,
      calendario: m.calendario,
      cronogramas: m.cronogramas,
      fluxogramas: m.fluxogramas,
      organogramas: m.organogramas,
      produtividade: m.produtividade,
      ultimoAcesso: ultimoAcesso ? new Date(ultimoAcesso).toLocaleDateString('pt-BR') : 'Nunca acessou',
      dias: m.dias ?? '—',
      notificacoes: m.notificacoesNaoLidas ?? '—',
      statusAcesso: m.statusAcessoLabel,
      nivelUtilizacao: m.nivelUtilizacao,
      observacoes: m.dias !== null && m.dias > 60 ? 'Sem acesso há mais de 60 dias — confirmar se ainda está ativo' : '',
    });
  });

  estilizarCabecalho(sheet);
  sheet.getColumn('produtividade').numFmt = '0.00';
  chavesCentralizadas(incluirNotificacoes).forEach((key) => {
    sheet.getColumn(key).alignment = { horizontal: 'center' };
  });

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const nivelCell = row.getCell('nivelUtilizacao');
    const nivel = nivelCell.value as NivelSemaforo;
    if (CORES_SEMAFORO[nivel]) {
      nivelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORES_SEMAFORO[nivel].fundo } };
      nivelCell.font = { bold: true, color: { argb: CORES_SEMAFORO[nivel].texto } };
    }
    const statusCell = row.getCell('statusAcesso');
    const corStatus = statusCell.value === 'Ativo' ? 'Alto' : statusCell.value === 'Em acompanhamento' ? 'Médio' : statusCell.value === 'Inativo' ? 'Baixo' : null;
    if (corStatus) {
      statusCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORES_SEMAFORO[corStatus].fundo } };
      statusCell.font = { bold: true, color: { argb: CORES_SEMAFORO[corStatus].texto } };
    }
  });

  zebra(sheet);
  autoWidth(sheet, { empresas: 22, observacoes: 40, nivelUtilizacao: 16, statusAcesso: 16 });
  sheet.autoFilter = { from: 'A1', to: `${sheet.getColumn(sheet.columns.length).letter}1` };

  return wb;
}
